import React from 'react'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.jsx'

export default function Launcher() {
  const { token } = useAuth()
  const [channels, setChannels] = React.useState([])
  const [unlocked, setUnlocked] = React.useState([])
  const [channel, setChannel] = React.useState('live')

  const [meta, setMeta] = React.useState(null)
  const [error, setError] = React.useState('')
  const [downloading, setDownloading] = React.useState(false)

  React.useEffect(() => {
    let live = true
    ;(async () => {
      try {
        const d = await api('/api/launcher/channels', token ? { token } : {})
        if (!live) return
        setChannels(Array.isArray(d.channels) ? d.channels : [])
        setUnlocked(Array.isArray(d.unlocked) ? d.unlocked : [])
      } catch (e) {
        if (live) setError(e.message || 'Unable to load channels.')
      }
    })()
    return () => { live = false }
  }, [token])

  React.useEffect(() => {
    let live = true
    setError('')
    api(`/api/launcher/meta?channel=${encodeURIComponent(channel)}`)
      .then((d) => { if (live) setMeta(d) })
      .catch((e) => { if (live) setError(e.message || 'Unable to load launcher metadata.') })
    return () => { live = false }
  }, [channel])

  const canAccess = channel === 'live' || unlocked.includes(channel)

  async function download() {
    setError('')
    setDownloading(true)
    try {
      const res = await fetch(`/api/launcher/download?channel=${encodeURIComponent(channel)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      })
      if (!res.ok) {
        const txt = await res.text().catch(() => '')
        throw new Error(txt || `Download failed (${res.status})`)
      }
      const blob = await res.blob()
      const filename = meta?.filename || 'DeltaReloadedLauncher.exe'
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (e) {
      setError(e.message || 'Download failed.')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-12">
      <div className="text-xs tracking-[0.32em] text-white/60">DOWNLOAD</div>
      <h1 className="mt-2 text-3xl md:text-5xl font-semibold">Launcher</h1>
      <p className="mt-4 text-white/60 max-w-3xl">
        Download the DeltaReloaded launcher. If you have beta access, additional channels will appear here automatically.
      </p>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-white/10 bg-panel/60 glow p-6 md:col-span-2">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="text-xs tracking-[0.28em] text-white/50">CHANNEL</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {channels.length ? channels.map((c) => {
                  const isLocked = c.protected && !unlocked.includes(c.id) && c.id !== 'live'
                  return (
                    <button
                      key={c.id}
                      onClick={() => setChannel(c.id)}
                      className={
                        "rounded-xl px-4 py-2 text-sm border " +
                        (channel === c.id
                          ? "border-white/25 bg-white/10 text-white"
                          : "border-white/10 bg-black/20 text-white/70 hover:border-white/20")
                      }
                    >
                      {c.id}
                      {isLocked ? <span className="ml-2 text-xs text-white/40">locked</span> : null}
                    </button>
                  )
                }) : (
                  <span className="text-sm text-white/60">Loading channels…</span>
                )}
              </div>

              <div className="mt-4 text-2xl font-semibold">{meta ? meta.name : 'DeltaReloaded Launcher'}</div>
              <div className="mt-1 text-white/60">Version: <span className="text-white/80">{meta ? meta.version : '...'} </span></div>
              {meta?.protected ? (
                <div className="mt-2 text-xs text-white/50">This channel is protected (login + entitlement required).</div>
              ) : null}
            </div>

            <button
              onClick={download}
              disabled={downloading || !canAccess}
              className="rounded-xl border border-white/15 bg-white/10 px-5 py-3 text-sm hover:bg-white/15 disabled:opacity-50"
              title={!canAccess ? 'Redeem a beta key in Account to unlock this channel.' : ''}
            >
              {downloading ? 'Downloading…' : 'Download'}
            </button>
          </div>

          {error && <div className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">{error}</div>}

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Info label="SHA256" value={meta?.sha256 || '—'} />
            <Info label="Released" value={meta?.released || '—'} />
            <Info label="Size" value={meta?.size || '—'} />
            <Info label="Notes" value={meta?.notes || '—'} />
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-panel/60 glow p-6">
          <div className="text-xs tracking-[0.28em] text-white/50">LAUNCHER WORKFLOW</div>
          <ol className="mt-3 space-y-2 text-sm text-white/60 list-decimal list-inside">
            <li>Open <span className="text-white/80">→ Launcher</span> and update the Launcher.</li>
            <li>The Beta Key That Was Given To you to access the beta.</li>
            <li>If You Have Problems <span className="text-white/80">Ask For Help Or Ask For Support</span> For Your Beta Key.</li>
            <li>Users redeem key in <span className="text-white/80">Account</span> to unlock channels.</li>
          </ol>
          <div className="mt-5 text-xs text-white/45">
            In production, downloads are streamed via the backend so protected channels stay private.
          </div>
        </div>
      </div>

      <section className="mt-10 rounded-2xl border border-white/10 bg-panel/60 glow p-6">
        <div className="text-xs tracking-[0.32em] text-white/60">NEXT</div>
        <h2 className="mt-2 text-2xl font-semibold">Suggested launcher features</h2>
        <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-white/60 text-sm">
          <li className="rounded-xl border border-white/10 bg-black/20 p-4">Patch manifests per channel (live/beta) with rollback support.</li>
          <li className="rounded-xl border border-white/10 bg-black/20 p-4">Delta patching + resume downloads.</li>
          <li className="rounded-xl border border-white/10 bg-black/20 p-4">File verification + repair (manifest-based SHA256).</li>
          <li className="rounded-xl border border-white/10 bg-black/20 p-4">Crash uploads + telemetry opt-in.</li>
        </ul>
      </section>
    </div>
  )
}

function Info({ label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/20 p-4">
      <div className="text-xs tracking-[0.28em] text-white/50">{label}</div>
      <div className="mt-2 text-sm text-white/70 break-all">{value}</div>
    </div>
  )
}
